import * as R from 'ramda'
import { computed, ComputedRef } from 'vue'

import {
  Confirmation,
  IncidentActionType,
} from '@collector/sportsapi-client-legacy'
import { ProbableIncident } from '@mobile/ActionQueue/Actions/Incidents/ProbableIncident'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import { useSportConfiguration } from '@mobile/views/Relation/Shared/RelationSportCommonDependencies/useSportConfiguration'

import { resolveIncidentTeamId } from './resolveIncidentTeamId'
import { Result } from './types'

type UseIncidentResult = {
  results: ComputedRef<Map<string, Result>>
  getResult: (incident: ProbableIncident) => Result | null
}

export function useIncidentResult(): UseIncidentResult {
  const { incidentsResultConfiguration } = useSportConfiguration()
  const {
    linkedProbableIncidents,
    probableHomeParticipant,
    probableAwayParticipant,
  } = useEventInjections()

  const sortedIncidents = computed(() =>
    R.reverse([...linkedProbableIncidents.value]),
  )

  const resultsMap = computed(() => {
    const results = new Map<string, Result>()
    if (!incidentsResultConfiguration) return results

    const { scoringIncidents, oppositeTeamScoringIncidents } =
      incidentsResultConfiguration
    let [homeTeamScore, awayTeamScore] = [0, 0]

    for (const { incident } of sortedIncidents.value) {
      const isCancelledOrDeleted =
        incident.confirmation === Confirmation.Cancelled ||
        incident.action === IncidentActionType.DELETE
      if (isCancelledOrDeleted) continue

      const scoringIncidentConfig = scoringIncidents.get(incident.incident_id)
      const oppositeTeamScoringIncidentConfig =
        oppositeTeamScoringIncidents?.get(incident.incident_id)

      const setHomeTeamScore = scoringIncidentConfig?.set
      const setAwayTeamScore = oppositeTeamScoringIncidentConfig?.set

      if (setHomeTeamScore !== undefined) {
        homeTeamScore = setHomeTeamScore
      }
      if (setAwayTeamScore !== undefined) {
        awayTeamScore = setAwayTeamScore
      }

      const incidentAttributes = incident.attribute_ids
      const scorePoints = scoringIncidents.get(incident.incident_id)?.add ?? 0
      const opponentPoints =
        oppositeTeamScoringIncidents?.get(incident.incident_id)?.add ?? 0

      const teamId = resolveIncidentTeamId(incident)
      const isHome = teamId === probableHomeParticipant.value?.id
      const isAway = teamId === probableAwayParticipant.value?.id

      if (scorePoints) {
        if (isHome) homeTeamScore += scorePoints
        else if (isAway) awayTeamScore += scorePoints
      } else if (opponentPoints) {
        if (isHome) awayTeamScore += opponentPoints
        else if (isAway) homeTeamScore += opponentPoints
      }

      if (incidentAttributes) {
        for (const attributeId of incidentAttributes) {
          const attributeScoreConfig =
            scoringIncidentConfig?.attributes?.get(attributeId)
          const oppositeTeamAttributeScoreConfig =
            oppositeTeamScoringIncidentConfig?.attributes?.get(attributeId)

          const attributeScorePoints = attributeScoreConfig?.add ?? 0
          const oppositeTeamAttributeScorePoints =
            oppositeTeamAttributeScoreConfig?.add ?? 0

          if (attributeScorePoints) {
            if (isHome) homeTeamScore += attributeScorePoints
            else if (isAway) awayTeamScore += attributeScorePoints
          } else if (oppositeTeamAttributeScorePoints) {
            if (isHome) awayTeamScore += oppositeTeamAttributeScorePoints
            else if (isAway) homeTeamScore += oppositeTeamAttributeScorePoints
          }
        }
      }

      results.set(incident.id, {
        firstTeamResult: homeTeamScore,
        secondTeamResult: awayTeamScore,
      })
    }

    return results
  })

  function getResult(incident: ProbableIncident): Result | null {
    const { isResultVisible } = incidentsResultConfiguration

    if (isResultVisible?.(incident) !== false) {
      return resultsMap.value.get(incident.id) ?? null
    }

    return null
  }

  return { results: resultsMap, getResult }
}
